import Vue from "vue";
import Prompt, {buttonType} from "@core/Components/Util/Prompt";

/**
 * Perform a prompt using the given modal
 * 
 * Clicking one of the buttons completes the prompt, closing the popup will trigger a `rejection`
 * @param {String|Vue} title Modal title
 * @param {String|Vue} body Modal body
 * @param {Object} options Modal options
 * @param {{ name: String, [icon]: String, class: String, emit: String, isRejection: Boolean }[]} options.buttons Buttons to show in the modal footer. `name` is the text displayed to the user. `icon` is an optional icon to display in front of the text. `class` will be used for styling. The `emit` value will be passed into the resolve hook upon clicking a button as an identifier. `isRejection` determines whether the given button will trigger a rejection instead of resolve upon clicking.
 * 
 * @returns {Promise<String>}
 */
async function promptViaComponent(title, body, options) {
  const el = document.createElement('div');
  document.body.appendChild(el);
  
  let vueInstance = null;

  const promptmise = new Promise((resolve, reject) => {
    //We assume a component has been passed in when it's a truthy and an object
    //todo: maybe check for `render`/`template` property?
    const titleIsComponent = title && typeof title === 'object';
    const bodyIsComponent = body && typeof body === 'object';
    
    const events = {};
    options.buttons.forEach(btn => events[btn.emit] = () => btn.isRejection ? reject() : resolve(btn.emit));
    
    vueInstance = new Vue({
      el: el,
      render: function (createElement) {
        return createElement(Prompt, {
          props: {
            title: titleIsComponent ? null : title,
            body: bodyIsComponent ? null : body,
            buttons: options.buttons,
            size: ""
          },
          on: {
            ...events,

            //hidden triggers prior to our events; schedule our rejection after the closing has been fully processed
            hidden: async () => {
              await vueInstance.$nextTick();
              reject();
            }
          },
          scopedSlots: {
            //Assigning a NULL value to a slot simply causes it to render its fallback content
            'title': () => titleIsComponent ? createElement(title) : null,
            'body': () => bodyIsComponent ? createElement(body) : null
          }
        });
      }
    });
  });

  try {
    return await promptmise;
  } finally {
    //Always cleanup after we're done
    vueInstance.$destroy();
    el.remove();
  }
}

async function promptYesNoViaComponent(title, body){
  await promptViaComponent(title, body, {
    buttons: [
      {
        ...buttonType.No,
        isRejection: true
      }, 
      buttonType.Yes
    ]
  });
}

export {
  buttonType,
  
  promptViaComponent as prompt,
  promptYesNoViaComponent as promptYesNo
}